import {RolesUtils, Sex, User} from "./user";
import {LocalModeratorPermissions} from "./room";

export const REGISTRATION_ROUTE = "registration"
export const EMAIL_CONFIRM_ROUTE = "registration/confirm"
export const RESTORE_ROUTE = "restore"
export const RESTORE_FINISH_ROUTE = "restore/finish"

export const MESSAGES_ALLOWED_TAGS = ['b', 'u', 'i', 'br']


export function guid() {
    function s4() {
        return Math.floor((1 + Math.random()) * 0x10000)
            .toString(16)
            .substring(1);
    }

    return s4() + s4() + '-' + s4() + '-' + s4() + '-' + s4() + '-' + s4() + s4() + s4();
}


export function removeElementFromArray<T>(arr: Array<T>, item: T) {
    for (let i = arr.length; i--;) {
        if (arr[i] === item) {
            arr.splice(i, 1);
        }
    }
}

export function removeElementFromArrayByIndex<T>(arr: Array<T>, index: number): T {
    return arr.splice(index, 1)[0]
}

// Возвращает случайное целое число между min (включительно) и max (не включая max)
// Использование метода Math.round() даст вам неравномерное распределение!
export function getRandomInt(min: number, max: number) {
    return Math.floor(Math.random() * (max - min)) + min;
}

export function getRandomBoolean(): boolean {
    return Math.random() >= 0.7
}

export function removeNonVisibleSymbols(string: string): string {
    //ㅤㅤㅤㅤ - содержит недопустимые символы
    return string
        .replace(new RegExp(/[\x00\x08\x0B\x0C\x0E-\x1F\u2063\u3164]/gu), '')
        .replace(new RegExp(/[\xA0\u1680\u2000-\u200A\u2028\u2029\u202F\u205F\u3000]/gu), '')
        .trim();
}

export function removeNonVisibleSymbolsV2(string: string): string {
    //https://unicodeplus.com/
    //https://stackoverflow.com/a/71459391/3319513
    return string.trim().replace(new RegExp(/[^ \p{LC}\p{Nd}\p{Nl}\p{P}\p{S}\p{M}\p{Cf}]/gu), '');
}

export function sanitizeText(purifiedText: string): string {
    let text = removeNonVisibleSymbols(purifiedText.trim())
    text = text.replace(/(<br>){2,}/gmi, '<br>')
    return text
}

export function isGenderAllowed(userSex: Sex, allowedGenders: Array<Sex>): boolean {
    return allowedGenders.includes(userSex)
}

export function isUserModerator(user: User): boolean {
    return user && RolesUtils.isModerator(user.roles)
}

export function isUserAdministrator(user: User): boolean {
    return user && RolesUtils.isAdministrator(user.roles)
}

/**
 * Раскладывает интервал на составляющие, дни, часы, минуты, секунды
 * @param interval - интервал в секундах
 */
export function getTimeRemaining(interval: number) {
    const seconds = Math.floor((interval / 1000) % 60);
    const minutes = Math.floor((interval / 1000 / 60) % 60);
    const hours = Math.floor((interval / (1000 * 60 * 60)) % 24);
    const days = Math.floor(interval / (1000 * 60 * 60 * 24));

    return {
        days,
        hours,
        minutes,
        seconds
    };
}

export function isDefined(value: any): boolean {
    return typeof value !== 'undefined' && value !== null;
}

export function formatString(str: string, ...val: string[]) {
    for (let index = 0; index < val.length; index++) {
        str = str.replace(`{${index}}`, val[index]);
    }
    return str;
}

export function stringToIntHash(str: string, upperbound: number = 500, lowerbound: number = 0): number {
    let result = 0;
    for (let i = 0; i < str.length; i++) {
        result = result + str.charCodeAt(i);
    }

    return (result % (upperbound - lowerbound)) + lowerbound;
}

const jsTemplateMatcher: RegExp = /\${\s?([^{}\s]*)\s?}|{{\s?([^{}\s]*)\s?}}/g;

export function formatStringJsTemplate(str: string, params: any): string {
    if (!params) {
        return str;
    }

    return str.replace(jsTemplateMatcher, (substring: string, p1: string, p2: string) => {
        let b = p1 || p2;
        let r = params[b];
        return isDefined(r) ? r : substring;
    });
}

export function isString(value: any): value is string {
    return typeof value === 'string' || value instanceof String;
}

/**
 * Полных лет на момент `now`. Единственное место, где возраст вычисляется: наружу с сервера уходит
 * только он, а сама дата рождения не покидает сервер (docs/specs/user-profile.md §3).
 *
 * Сравнение идёт по календарным компонентам, а не по разнице timestamp'ов: длительность года
 * непостоянна (високосные), и арифметика по миллисекундам ошибается на день у именинников.
 * Родившийся 29 февраля «взрослеет» 1 марта в невисокосный год — это то, что делает `getDate()`
 * при сравнении с 28-м числом.
 */
export function calculateAge(birthDate: Date, now: Date = new Date()): number {
    // Обе даты читаются UTC-геттерами: birthDate хранится как UTC-полночь (parseBirthDate), и в
    // таймзоне с отрицательным смещением локальный getDate() вернул бы предыдущий день.
    let age = now.getUTCFullYear() - birthDate.getUTCFullYear()

    const monthDiff = now.getUTCMonth() - birthDate.getUTCMonth()

    if (monthDiff < 0 || (monthDiff === 0 && now.getUTCDate() < birthDate.getUTCDate())) {
        age--
    }

    return age
}

/**
 * Дата рождения из ISO `yyyy-mm-dd` в UTC-полночь. Парсим вручную, а не `new Date(string)`:
 * нужен единый результат независимо от таймзоны сервера, иначе возраст у пользователя мог бы
 * отличаться на день в зависимости от того, где крутится процесс.
 */
export function parseBirthDate(value: string): Date | null {
    if (!isString(value)) {
        return null
    }

    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim())

    if (!match) {
        return null
    }

    const year = Number(match[1])
    const month = Number(match[2])
    const day = Number(match[3])

    const date = new Date(Date.UTC(year, month - 1, day))

    // Отсекает несуществующие даты (31 февраля): Date их «перекатывает» на следующий месяц.
    if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) {
        return null
    }

    return date
}

export function formatBirthDate(date: Date): string {
    const month = String(date.getUTCMonth() + 1).padStart(2, '0')
    const day = String(date.getUTCDate()).padStart(2, '0')

    return `${date.getUTCFullYear()}-${month}-${day}`
}
